import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import authService from "../../services/authService";

function ForcePasswordChangePage() {
  const navigate = useNavigate();
  const { user, refreshProfile } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const standardPassword = "Welcome2Journey!";

  useEffect(() => {
    if (user && !user.forcePasswordReset) {
      navigate("/", { replace: true });
    }
  }, [navigate, user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError("Please fill in all fields.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("New passwords do not match.");
      return;
    }

    if (newPassword.length < 8) {
      setError("New password must be at least 8 characters long.");
      return;
    }

    setLoading(true);
    try {
      const result = await authService.changePassword({
        currentPassword,
        newPassword,
        confirmNewPassword: confirmPassword,
      });

      if (!result.success) {
        setError(result.message || "Failed to change password.");
        return;
      }

      await refreshProfile();
      setSuccess(result.message || "Password changed successfully.");
      setTimeout(() => {
        navigate("/", { replace: true });
      }, 800);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to change password.");
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-green-cloud flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-lg card-brand-elevated overflow-hidden">
        <div className="bg-gradient-to-r from-sunrise-red to-sunrise-600 text-white px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center">
              <i className="fas fa-shield-alt text-xl"></i>
            </div>
            <div>
              <h1 className="text-2xl font-bold">Change your password</h1>
              <p className="text-white/85 text-sm">
                {user?.firstName || "Apprentice"}, your account requires an immediate password update.
              </p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-5">
          <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-xl p-4 text-sm leading-relaxed">
            <div className="font-semibold mb-1">Standard password</div>
            <div className="break-all font-mono text-base">{standardPassword}</div>
            <p className="mt-2 text-xs text-amber-800">
              You must change this password before you can continue using Journey.
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
              <i className="fas fa-exclamation-triangle"></i>
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
              <i className="fas fa-check-circle"></i>
              <span>{success}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-grey-dusk mb-2">
                Current password
              </label>
              <input
                type="password"
                className="input-brand w-full"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter the standard password"
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-grey-dusk mb-2">
                New password
              </label>
              <input
                type="password"
                className="input-brand w-full"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Choose a new password"
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-grey-dusk mb-2">
                Confirm new password
              </label>
              <input
                type="password"
                className="input-brand w-full"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repeat the new password"
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-xl bg-sunrise-red text-white font-semibold px-5 py-3.5 shadow-sm hover:bg-sunrise-600 hover:shadow-md transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? "Updating password..." : "Change password"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ForcePasswordChangePage;
